import React from 'react';
import './banner.css'; 
import { Carousel, CarouselItem, CarouselCaption, CarouselControl, CarouselIndicators } from 'reactstrap';
import showdown from 'showdown';
import Banner from '../../../models/Banner';
import { useTheme } from '../../../styles/useTheme';
import styled from 'styled-components';

// define Announcement json structure
// type Announcement = {
//     title: string;
//     body: string;
//     footer: string;
//     start_date: string;
//     type: string;
//     author: string;
//     end_date: string;
// }

type BannerProps = {
    announcements: Banner[]
}

const AnnouncementsBanner: React.FC<BannerProps> = ({announcements}) => {
    const [theme] = useTheme();

    const [activeIndex, setActiveIndex] = React.useState(0);
    const [animating, setAnimating] = React.useState(false);

    const next = () => {
        if (animating) return;
        const nextIndex = activeIndex === announcements.length - 1 ? 0 : activeIndex + 1;
        setActiveIndex(nextIndex);
    };

    const previous = () => {
        if (animating) return;
        const nextIndex = activeIndex === 0 ? announcements.length - 1 : activeIndex - 1;
        setActiveIndex(nextIndex);
    };

    const onExiting = () => setAnimating(true);
    const onExited = () => setAnimating(false);

    const goToIndex = (newIndex: number) => {
        if (animating) return;
        setActiveIndex(newIndex);
    };

    return (
        <div className={`announcement-banner theme-${theme.type}`}>
            <Carousel
                className='announcement-carousel'
                activeIndex={activeIndex}
                next={next}
                previous={previous}
                interval={60000} // Set built-in auto-advance timer to 60 seconds
            >
                {announcements.length > 1 && (
                    <CarouselIndicators
                        items={announcements}
                        activeIndex={activeIndex}
                        onClickHandler={goToIndex}
                    />
                )}
                {announcements.map((announcement, index) => (
                    <StyledCarouselItem
                        key={`${index}-${theme.type}`}
                        onExiting={onExiting}
                        onExited={onExited}
                        className="carousel-item"
                        announcementType={announcements[index].type}
                        style={{
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            minHeight: '200px'
                        }}
                    >
                        <CarouselCaption
                            captionText={
                                <div 
                                    style={{
                                        color: theme.banner.textColor,
                                    }}>
                                    <h4 className='title'>
                                        {/*
                                            Use showdown to convert markdown to HTML, then dangerouslySetInnerHTML to render.
                                            This avoids using ReactMarkdown.
                                        */}
                                        {(() => {
                                            const converter = new showdown.Converter({
                                                openLinksInNewWindow: true,
                                                parseImgDimensions: true,
                                                simplifiedAutoLink: true
                                            });
                                            const html = converter.makeHtml(announcement.title || '');
                                            return (
                                                <span
                                                    dangerouslySetInnerHTML={{ __html: html }}
                                                />
                                            );
                                        })()}
                                    </h4>
                                    <p className='body'>
                                        {(() => {
                                            const converter = new showdown.Converter({
                                                openLinksInNewWindow: true,
                                                parseImgDimensions: true,
                                                simplifiedAutoLink: true
                                            });
                                            const html = converter.makeHtml(announcement.body || '');
                                            return (
                                                <span
                                                    dangerouslySetInnerHTML={{ __html: html }}
                                                />
                                            );
                                        })()}
                                    </p>
                                    <p className='date' style={{
                                        color: theme.banner.textColor,
                                    }}>
                                        {(() => {
                                            const converter = new showdown.Converter({
                                                openLinksInNewWindow: true,
                                                parseImgDimensions: true,
                                                simplifiedAutoLink: true
                                            });
                                            const html = converter.makeHtml(announcement.footer || '');
                                            return (
                                                <span
                                                    dangerouslySetInnerHTML={{ __html: html }}
                                                />
                                            );
                                        })()}
                                    </p>
                                    <div className="info-container">
                                        <p className='date' style={{
                                        color: theme.banner.textColor,
                                    }}><i>Posted By: {announcement.author}</i></p>
                                        <p style={{
                                        color: theme.banner.textColor,
                                    }}>|</p>
                                        <p className='date' style={{
                                        color: theme.banner.textColor,
                                    }}><i>Date Posted: {announcement.start_date}</i></p>
                                    </div>
                                </div>
                            }
                        />
                    </StyledCarouselItem>
                ))}
                {announcements.length > 1 && (
                    <>
                        <CarouselControl
                            direction="prev"
                            directionText="Previous"
                            onClickHandler={previous}
                            style={{
                                color: theme.type === 'dark' ? '#ffffff' : '#000000'
                            }}
                        />
                        <CarouselControl
                            direction="next"
                            directionText="Next"
                            onClickHandler={next}
                            style={{
                                color: theme.type === 'dark' ? '#ffffff' : '#000000'
                            }}
                        />
                    </>
                )}
            </Carousel>
        </div>
    );
}

const StyledCarouselItem = styled(CarouselItem)<{ announcementType?: string }>`
  background-color: ${({ theme, announcementType }) => {
    const isDark = theme.type === 'dark';
    switch (announcementType) {
      case 'red-freeze':
        return isDark ? 'rgba(122, 35, 35, 0.5)' : 'rgba(255, 204, 204, 1.0)';
      case 'yellow-freeze':
        return isDark ? 'rgba(119, 105, 3, 0.5)' : 'rgba(255, 255, 204, 1.0)';
      case 'orange-freeze':
        return isDark ? 'rgba(221, 108, 3, 0.5)' : 'rgba(255, 229, 204, 1.0)';
      default:
        return isDark ? 'rgba(32, 32, 32, 1.0)' : 'rgba(223, 223, 223, 1.0)';
    }
  }};
  
`;

export default AnnouncementsBanner;